import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BarChart2, LayoutDashboard, FileText, Lock, ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import DashboardGallery from "./DashboardGallery";
import ReportLibrary from "./ReportLibrary";
import DashboardView from "./DashboardView";
import TicketDashboard from "@/modules/ticketing/TicketDashboard";
import TicketReports from "@/modules/ticketing/TicketReports";

// Sentinel ids for the injected ticketing entries (negative so they never collide
// with real analytics dashboard/report ids).
const TICKETING_DASHBOARD_ID = -901;
const TICKETING_REPORT_ID = -902;

export default function Analytics() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("dashboards");
  const [openDashboardId, setOpenDashboardId] = useState<number | null>(null);
  const [openTicketView, setOpenTicketView] = useState<"dashboard" | "report" | null>(null);
  const { canView, canCreate, canExport } = usePermissions("analytics");
  const { canView: canViewTickets } = usePermissions("tickets");
  const allowed = canView || canViewTickets;

  // Synthetic "Ticketing" entries injected into the dashboard/report lists so they
  // open just like the built-in system ones.
  const ticketingDashboardEntry = {
    id: TICKETING_DASHBOARD_ID,
    name: "Ticketing Operations",
    description: "Ticket volume, SLA compliance, MTTR & CSAT",
    isSystem: true,
    createdAt: new Date(0),
  };
  const ticketingReportEntry = {
    id: TICKETING_REPORT_ID,
    name: "Ticketing Report",
    description: "Backlog, SLA compliance, agent workload & CSAT",
    dataSource: "tickets",
    isSystem: true,
    definition: null,
    createdAt: new Date(0),
  };

  // Seed system data on first load
  const seedMutation = trpc.analytics.seed.seedSystemData.useMutation({
    onSuccess: (data) => {
      if (data.seeded) {
        toast.success(`Analytics seeded: ${data.reports} reports, ${data.dashboards} dashboards`);
      }
    },
    onError: () => {
      // Silently fail — already seeded or DB unavailable
    },
  });

  useEffect(() => {
    if (canView) seedMutation.mutate();
  }, [canView]);

  // If user has neither analytics nor ticket view access, show access denied
  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4 text-center px-6">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
          <Lock className="w-7 h-7 text-muted-foreground" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('errors.accessDenied')}</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            {t('errors.permissionDenied')}
          </p>
        </div>
      </div>
    );
  }

  // When a ticketing entry is opened, render it full-screen with a back bar
  if (openTicketView !== null) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="border-b bg-card px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => setOpenTicketView(null)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {openTicketView === "dashboard" ? "Dashboards" : "Reports"}
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {openTicketView === "dashboard" ? <TicketDashboard /> : <TicketReports />}
        </div>
      </div>
    );
  }

  // When a dashboard is open, render it full-screen (no Analytics header/tabs)
  if (openDashboardId !== null) {
    return (
      <DashboardView
        dashboardId={openDashboardId}
        onBack={() => setOpenDashboardId(null)}
      />
    );
  }

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6 flex flex-col">
      {/* Page Header */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10">
              <BarChart2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">{t('nav.analytics')}</h1>
              <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1">
          <div className="border-b bg-card px-6">
            <TabsList className="h-12 bg-transparent p-0 gap-0 border-none shadow-none">
              <TabsTrigger
                value="dashboards"
                className="h-12 rounded-none border-0 border-b-2 border-transparent bg-transparent shadow-none outline-none ring-0 focus-visible:ring-0 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 gap-2 text-sm font-medium text-muted-foreground data-[state=active]:text-foreground"
              >
                <LayoutDashboard className="w-4 h-4" />
                {t('nav.dashboard')}
              </TabsTrigger>
              <TabsTrigger
                value="reports"
                className="h-12 rounded-none border-0 border-b-2 border-transparent bg-transparent shadow-none outline-none ring-0 focus-visible:ring-0 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 gap-2 text-sm font-medium text-muted-foreground data-[state=active]:text-foreground"
              >
                <FileText className="w-4 h-4" />
                {t('nav.reports')}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="dashboards" className="flex-1 overflow-auto m-0">
            <DashboardGallery
              canCreate={canCreate}
              prependSystem={canViewTickets ? [ticketingDashboardEntry] : []}
              onOpenDashboard={(id) =>
                id === TICKETING_DASHBOARD_ID
                  ? setOpenTicketView("dashboard")
                  : setOpenDashboardId(id)
              }
            />
          </TabsContent>

          <TabsContent value="reports" className="flex-1 overflow-auto m-0">
            <ReportLibrary
              canCreate={canCreate}
              canExport={canExport}
              prependReports={canViewTickets ? [ticketingReportEntry] : []}
              onOpenReport={(id) => {
                if (id === TICKETING_REPORT_ID) {
                  setOpenTicketView("report");
                  return true;
                }
                return false;
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
